import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { client } from "../../..";
import { WA_LINK } from "../../../Queries/WhatsApp";
import { upsertWaConversation, fetchWaConversation } from "../../../Slices/whatsappSlice";
import SidePanel from "./SidePanel";
import RecordSearch from "./RecordSearch";
import { gqlErrorMessage } from "./waUtils";

/** Link a conversation to an existing lead (and contact) or client. */
const LinkRecordPanel = ({ conversation, candidates, onClose }) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async (pick = selected) => {
    if (!pick || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await client.mutate({
        mutation: WA_LINK,
        variables: {
          id: conversation.id,
          leadId: pick.kind === "lead" ? pick.id : null,
          leadContactId: pick.kind === "lead" ? pick.contact?.id || null : null,
          clientId: pick.kind === "client" ? pick.id : null,
        },
      });
      dispatch(upsertWaConversation(data.whatsappLinkConversation));
      dispatch(fetchWaConversation(conversation.id));
      onClose();
    } catch (err) {
      setError(gqlErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const suggestions = [
    ...(candidates?.leads || []).map((l) => ({ kind: "lead", id: String(l.leadId), title: l.agencyName || "Lead", contact: l.leadContactId ? { id: String(l.leadContactId), name: l.contactName } : null })),
    ...(candidates?.clients || []).map((c) => ({ kind: "client", id: String(c.clientId), title: c.clientName || "Client", contact: null })),
  ];

  return (
    <SidePanel
      title="Link to record"
      subtitle={`Attach ${conversation.contactName || conversation.displayNumber} to an existing lead or client — the thread then shows on that record.`}
      onClose={onClose}
      footer={
        <>
          {error && <span className="wa-help" style={{ color: "var(--wa-danger)" }}>{error}</span>}
          <button type="button" className="wa-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="wa-btn wa-btn--primary" disabled={!selected || saving} onClick={() => save()}>
            {saving ? <i className="fa-solid fa-spinner wa-spin" /> : <i className="fa-solid fa-link" />} Link
          </button>
        </>
      }
    >
      {suggestions.length > 0 && (
        <div className="wa-block">
          <h4><i className="fa-solid fa-wand-magic-sparkles" /> Records with this phone number</h4>
          {suggestions.map((s) => (
            <div className="wa-result__contact" key={`${s.kind}-${s.id}`} style={{ borderTop: "none", padding: "6px 0" }}>
              <div>
                <b>{s.title}</b> <span className={`wa-chip ${s.kind === "lead" ? "wa-chip--accent" : "wa-chip--ok"}`}>{s.kind}</span>
                {s.contact?.name && <small>{s.contact.name}</small>}
              </div>
              <button type="button" className="wa-btn wa-btn--sm" disabled={saving} onClick={() => save(s)}>Link</button>
            </div>
          ))}
        </div>
      )}
      <div className="wa-label">Search all leads and clients</div>
      <RecordSearch requireContact={false} selected={selected} onPick={setSelected} autoFocus={!suggestions.length} />
      {selected && (
        <div className="wa-alert wa-alert--info" style={{ marginTop: 8 }}>
          <i className="fa-solid fa-circle-check" /> Selected: <b>{selected.title}</b>{selected.contact?.name ? ` — ${selected.contact.name}` : ""}
        </div>
      )}
    </SidePanel>
  );
};

export default LinkRecordPanel;
